import React, { useEffect, useState } from "react"
import {graphql, useStaticQuery} from "gatsby"

import xStyles from './x_by_y.module.scss'
import { VictoryAxis, VictoryChart, VictoryScatter } from "victory"
import ScatterImage from "../helper_js/scatter_image"
import Select from "react-select"
import LinearRegression from "../helper_js/linear_regression"
import FetchRegressionData from "../helper_js/fetch_regression_data"

const XbyY = () => {
    const data = useStaticQuery(graphql`
        query {
            allTeamsCsv {
                nodes {
                    season
                    team
                    nfelo
                    wins
                    pythag_wins
                    expected_wins_538_ytd
                    expected_wins_pff_ytd
                    points_for_ytd
                    points_against_ytd
                    point_dif_ytd
                    wepa_margin_ytd
                    film_dif_ytd
                    epa_per_play_ytd
                    epa_per_pass_ytd
                    epa_per_rush_ytd
                    epa_per_play_against_ytd
                    epa_per_pass_against_ytd
                    epa_per_rush_against_ytd
                    net_epa_per_play_ytd
                    wepa_for_ytd
                    wepa_against_ytd
                    qb_value_538_ytd
                    team_nick
                    team_logo_espn
                }
            }
            justYears: allTeamsCsv {
                distinct (
                    field: season
                )
            }
        }
    `)

    const dropOptionsMetrics = [
        {'value' : 'nfelo', 'label' : 'nfelo',},
        {'value' : 'wins', 'label' : 'Wins',},
        {'value' : 'pythag_wins', 'label' : 'Pythagorean Wins',},
        {'value' : 'expected_wins_538_ytd', 'label' : '538 Expected Wins',},
        {'value' : 'expected_wins_pff_ytd', 'label' : 'Film Grade Expected Wins',},
        {'value' : 'points_for_ytd', 'label' : 'Points For',},
        {'value' : 'points_against_ytd', 'label' : 'Points Against',},
        {'value' : 'point_dif_ytd', 'label' : 'Point Differential',},
        {'value' : 'wepa_margin_ytd', 'label' : 'WEPA Point Differential',},
        {'value' : 'epa_per_play_ytd', 'label' : 'EPA / Play (Off)',},
        {'value' : 'epa_per_pass_ytd', 'label' : 'EPA / Pass (Off)',},
        {'value' : 'epa_per_rush_ytd', 'label' : 'EPA / Rush (Off)',},
        {'value' : 'epa_per_play_against_ytd', 'label' : 'EPA / Play (Against)',},
        {'value' : 'epa_per_pass_against_ytd', 'label' : 'EPA / Pass (Against)',},
        {'value' : 'epa_per_rush_against_ytd', 'label' : 'EPA / Rush (Against)',},
        {'value' : 'wepa_for_ytd', 'label' : 'WEPA (Off)',},
        {'value' : 'wepa_against_ytd', 'label' : 'WEPA (Against)',},
        {'value' : 'net_epa_per_play_ytd', 'label' : 'Net EPA / Play',},
        {'value' : 'qb_value_538_ytd', 'label' : '538 Avg QB Value',},
    ]

    const dropOptionsSeasons = data.justYears.distinct.map((node) => {
        return(
            {'value' : parseInt(node), 'label' : parseInt(node)}
        )
    })

    const [selectedX, setSelectedX] = useState(dropOptionsMetrics[4])
    const [selectedY, setSelectedY] = useState(dropOptionsMetrics[0])
    const [selectedSeason, setSelectedSeason] = useState(dropOptionsSeasons[dropOptionsSeasons.length-1])

    const [fetchedX, setFetchedX] = useState([])
    const [fetchedY, setFetchedY] = useState([])

    useEffect(() => {
        fetchData(data, selectedSeason, selectedX, selectedY)
    }, [data, selectedSeason, selectedX, selectedY])

    const fetchData = async (data, selectedSeason, selectedX, selectedY) => {
        await FetchRegressionData(data, selectedSeason, selectedX, selectedY).then(
            response => {
                setFetchedX(response[0])
                setFetchedY(response[1])
            }
        )
    }
    

    return (

        <div className={xStyles.containerWrapper}>
            <h2 className={xStyles.section_header}>
                About X by Y
            </h2>
            <div className={xStyles.about_div}>
                <p className={xStyles.about_text}>
                    X by Y is a tool that allows you explore the relationship between two metrics in a given year. Metrics are selectable via the drop down menus.
                </p>
                <p className={xStyles.about_text}>
                    The relationship of the two selected metrics is measured by the degree to which they correlate (rsquared). This relationship is more illustrative than statistical or causal since it is constrained to a single year.
                </p>
            </div>
            <div className={xStyles.spacer_div}></div>
            <div className={xStyles.selectModuleContainer}>
                <div className={xStyles.selectModuleSectionContainer}>
                    <h3 className={xStyles.selectModuleHeader}>
                        Select Season
                    </h3>
                    <div className={xStyles.selectContainerDiv}>
                        <Select
                            options={dropOptionsSeasons}
                            onChange={setSelectedSeason}
                            placeholder='Select Season'
                        />
                    </div>
                </div>
                <div className={xStyles.selectModuleSectionContainer}>
                    <h3 className={xStyles.selectModuleHeader}>
                        Comparing...
                    </h3>
                    <div className={xStyles.selectModuleSubSectionContainer}>
                        <div className={xStyles.metricSelectContainer}>
                            <p className={xStyles.metricSelectP}>
                                X
                            </p>
                            <div className={xStyles.selectContainerDiv}>
                                <Select
                                    options={dropOptionsMetrics}
                                    onChange={setSelectedX}
                                    placeholder='Select X'
                                />
                            </div>
                        </div>
                        <div className={xStyles.innerCompareContainer}>
                            <p className={xStyles.innerCompareP}>
                                by...
                            </p>
                        </div>
                        <div className={xStyles.metricSelectContainer}>
                            <p className={xStyles.metricSelectP}>
                                Y
                            </p>
                            <div className={xStyles.selectContainerDiv}>
                                <Select
                                    options={dropOptionsMetrics}
                                    onChange={setSelectedY}
                                    placeholder='Select Y'
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={xStyles.containerDiv}>
                <div className={xStyles.chartContainerDiv}>
                    {fetchedY.length === 32 && (
                        <VictoryChart
                            domainPadding={{
                                y:[0,0],
                                x:[0,0]
                            }}
                            width={550}
                            height={400}
                        >
                            <VictoryAxis
                                label={selectedX['label']}
                                style={{
                                    axisLabel: {
                                        fontSize: '10px',
                                        padding: '30',
                                        fontWeight: "bold"
                                    },
                                    tickLabels: {
                                        fontSize: '8px',
                                        color: '#808080',
                                    },
                                }}
                                orientation='bottom'
                                offsetY={50}
                            />
                            <VictoryAxis
                                dependentAxis
                                label={selectedY['label']}
                                style={{
                                    axisLabel: {
                                        fontSize: '10px',
                                        padding: '40',
                                        fontWeight: "bold"
                                    },
                                    tickLabels: {
                                        fontSize: '8px',
                                        color: '#808080',
                                    },
                                }}
                                offsetX={50}
                            />
                            {LinearRegression({x_array : fetchedX, y_array : fetchedY})}
                            {data.allTeamsCsv.nodes.map((node) => {
                                if (parseInt(node['season']) === selectedSeason['value']) {
                                    const victoryData = [
                                        {
                                            x: Math.round(10000*node[selectedX['value']])/10000,
                                            y: Math.round(10000*node[selectedY['value']])/10000,
                                        }
                                    ]
                                    return(
                                        <VictoryScatter
                                            dataComponent={
                                                <ScatterImage
                                                    logoUrl={node.team_logo_espn}
                                                    team={node.team}
                                                    season={node.season}
                                                    includeSeason={false}
                                                />
                                            }
                                            data={victoryData}
                                            x='x'
                                            y='y'
                                        />
                                    )
                                } else {
                                    return(null)
                                }
                            })}
                        </VictoryChart>
                    )}
                </div>
            </div>
        </div>
    )

}

export default XbyY
